/**
 * Registry of internal synthetic producers (ENG-5491).
 *
 * An internal synthetic is activity from a test, canary, or monitor that we
 * operate and can identify explicitly. Rows it produces are marked with the
 * registered source at capture so customer usage can exclude them with one
 * shared marker and registered-actor predicate while diagnostic queries keep them.
 *
 * Marking is explicit registration, never inference: an employee's ordinary
 * use is internal but not synthetic, a headless browser is a traffic label
 * and not a synthetic, and an unregistered actor stays unmarked. Adding a
 * producer is a code change here, reviewed like any other capture change.
 *
 * Sources are identified three ways, one per surface:
 *   - a beacon or request marker the producer sets itself (web heartbeat),
 *   - a command or event-id convention (CLI ingest canary),
 *   - a Clerk actor — user id, org id, or an id prefix minted by a test
 *     harness (Production AX Canary, shared Clerk test orgs, acceptance
 *     fixtures).
 */

export const SYNTHETIC_SOURCE_IDS = [
  "web_capture_heartbeat",
  "cli_ingest_canary",
  "production_ax_canary",
  "clerk_test_org",
  "acceptance_suite",
] as const;

export type SyntheticSourceId = (typeof SYNTHETIC_SOURCE_IDS)[number];

export type SyntheticSurface = "web" | "cli" | "platform";

export type SyntheticSourceDefinition = {
  id: SyntheticSourceId;
  description: string;
  surfaces: readonly SyntheticSurface[];
  /**
   * Clerk actors that identify this producer on every surface where the
   * request carries identity. Exact ids or minted prefixes; prefixes must be
   * specific enough that no customer id can start with them.
   */
  actors?: {
    userIds?: readonly string[];
    orgIds?: readonly string[];
    userIdPrefixes?: readonly string[];
    orgIdPrefixes?: readonly string[];
  };
};

/** Marker the Datadog browser Synthetics put on the real beacon (ENG-4224). */
export const WEB_CAPTURE_HEARTBEAT_UTM_CAMPAIGN = "axp_web_capture_heartbeat";

/** Production AX Canary: the Cursor automation's Clerk identity and org. */
export const PRODUCTION_AX_CANARY_USER_ID = "user_3Gv1G78XBX8bW06t14kFjqSF1nh";
export const PRODUCTION_AX_CANARY_ORG_ID = "org_3H68Gy0bmMCNT4IoRsyNKpi7uyI";

/** Shared Clerk dev-instance test orgs (HOSTED_E2E_CONTRACT.ci.testOrgId, test_org_2). */
export const CLERK_TEST_ORG_IDS = [
  "org_3IViyPlf0PKI4DKMVwth6zx3SDY",
  "org_3Ht4wQqpnlTui4CGISomB2DnkJw",
] as const;

/** Id prefixes minted by the repository acceptance suites for fixture actors. */
export const ACCEPTANCE_ORG_ID_PREFIXES = ["org_acceptance_"] as const;
export const ACCEPTANCE_USER_ID_PREFIXES = [
  "user_acceptance_",
  "user_creator_",
  "user_member_",
  "user_inviter_",
  "user_invitee_",
] as const;

export const SYNTHETIC_SOURCES: Readonly<Record<SyntheticSourceId, SyntheticSourceDefinition>> = {
  web_capture_heartbeat: {
    id: "web_capture_heartbeat",
    description:
      "Datadog browser Synthetics heartbeat that loads each site with utm_campaign=axp_web_capture_heartbeat. The beacon marks every event of that session; ingest diverts them to web_capture_heartbeats.",
    surfaces: ["web"],
  },
  cli_ingest_canary: {
    id: "cli_ingest_canary",
    description:
      "Hourly CLI ingest canary (command axp.ingest.canary, event ids axp-cli-canary-*) written through the real authenticated CLI ingest path.",
    surfaces: ["cli"],
  },
  production_ax_canary: {
    id: "production_ax_canary",
    description:
      "Production AX Canary: the Cursor automation that runs a real experiment through the CLI and platform every 30 minutes.",
    surfaces: ["platform", "cli", "web"],
    actors: {
      userIds: [PRODUCTION_AX_CANARY_USER_ID],
      orgIds: [PRODUCTION_AX_CANARY_ORG_ID],
    },
  },
  clerk_test_org: {
    id: "clerk_test_org",
    description:
      "Shared Clerk dev-instance test organizations used by e2e and QA sessions; memberships are created for every test-key signup.",
    surfaces: ["platform", "cli", "web"],
    actors: { orgIds: CLERK_TEST_ORG_IDS },
  },
  acceptance_suite: {
    id: "acceptance_suite",
    description:
      "Repository acceptance suites: fixture orgs and users minted with the registered id prefixes.",
    surfaces: ["platform", "cli", "web"],
    actors: {
      orgIdPrefixes: ACCEPTANCE_ORG_ID_PREFIXES,
      userIdPrefixes: ACCEPTANCE_USER_ID_PREFIXES,
    },
  },
};

export function isSyntheticSourceId(value: unknown): value is SyntheticSourceId {
  return typeof value === "string" && (SYNTHETIC_SOURCE_IDS as readonly string[]).includes(value);
}

/** Sources a producer may declare on its own events for the given surface. */
export function syntheticSourcesForSurface(surface: SyntheticSurface): SyntheticSourceId[] {
  return SYNTHETIC_SOURCE_IDS.filter((id) => SYNTHETIC_SOURCES[id].surfaces.includes(surface));
}

export type SyntheticActor = {
  userId?: string | null;
  orgId?: string | null;
};

/**
 * The registered source for a request-time actor, or null when neither the
 * user nor the organization is registered. Registered ids are matched exactly;
 * prefixes match the start of the id. An unregistered actor is never guessed.
 */
export function resolveSyntheticSourceForActor(actor: SyntheticActor): SyntheticSourceId | null {
  const userId = actor.userId ?? "";
  const orgId = actor.orgId ?? "";
  for (const id of SYNTHETIC_SOURCE_IDS) {
    const actors = SYNTHETIC_SOURCES[id].actors;
    if (!actors) continue;
    if (userId && actors.userIds?.includes(userId)) return id;
    if (orgId && actors.orgIds?.includes(orgId)) return id;
    if (userId && actors.userIdPrefixes?.some((prefix) => userId.startsWith(prefix))) return id;
    if (orgId && actors.orgIdPrefixes?.some((prefix) => orgId.startsWith(prefix))) return id;
  }
  return null;
}

/** SQL/HogQL expressions are supplied by trusted query builders, never requests.
 * Both engines support coalesce, NOT IN and startsWith. The same registry
 * excludes historical unmarked actors on either side of a parity comparison.
 */
export function syntheticActorExclusionSql(
  userExpression: string | null,
  orgExpression: string | null,
): string {
  const literal = (value: string) => `'${value.replaceAll("\\", "\\\\").replaceAll("'", "\\'")}'`;
  const clauses: string[] = [];
  for (const id of SYNTHETIC_SOURCE_IDS) {
    const actors = SYNTHETIC_SOURCES[id].actors;
    if (!actors) continue;
    for (const [expression, ids, prefixes] of [
      [userExpression, actors.userIds, actors.userIdPrefixes],
      [orgExpression, actors.orgIds, actors.orgIdPrefixes],
    ] as const) {
      if (!expression) continue;
      const actor = `coalesce(${expression}, '')`;
      if (ids?.length) clauses.push(`${actor} NOT IN (${ids.map(literal).join(", ")})`);
      for (const prefix of prefixes ?? [])
        clauses.push(`NOT startsWith(${actor}, ${literal(prefix)})`);
    }
  }
  return clauses.join(" AND ") || "1 = 1";
}
